/**
 * Copyright © Veeam Software Group GmbH.
 */

import { ACTION_VIEW, TextColumn, SortingMode } from '@veeam-vspc/components';
import { capitalize, deepCopy } from '@veeam-vspc/core';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';

import type { WizardStepData, GridStore } from '@veeam-vspc/components';

import { AdvancedGrid } from 'views/components/AdvancedGrid';
import { SidePanelForm } from 'components/layouts/SidePanelForm';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useResellerWizardStore } from '../../../../hooks';
import { ActionsToolbar } from './components/ActionsToolbar';
import { getVboProxy } from './utils';
import { getVb365RepositoryStorageTypes } from 'core/utils';

import type { ResellerVboResource, ResellerData } from '../../../../interfaces';
import type { VboServerRepository } from './types';

export interface Microsoft365RepositoriesProps extends WizardStepData<ResellerData> {
    hidePanel: () => void;
}

export const Microsoft365Repositories = observer((props: Microsoft365RepositoriesProps) => {
    const lang = useLang();
    const { hidePanel, data, onDataChange } = props;
    const api = useRef<GridStore<VboServerRepository, void, void>>();
    const wizardStore = useResellerWizardStore();

    const [vb365RepositoryStorageTypes] = useState(getVb365RepositoryStorageTypes(lang));

    const [repositories] = useState(() => {
        const resources = deepCopy(data.vboResources) as ResellerVboResource[];
        const result: VboServerRepository[] = [];
        resources.forEach(resource => result.push(...resource.vboRepositories.map(rep => ({ ...rep, vboInstanceUid: resource.vboInstanceUid }))));
        return result;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            wizardStore.availableVboBackupRepositoryCache.load(),
            wizardStore.availableVboProxiesCache.load(),
        ])
            .then(() => setLoading(false));
    }, []);

    return (
        <SidePanelForm
            onRequestClose={hidePanel}
            title={capitalize(lang.VEEAM_BACKUP_FOR_MICROSOFT_365_REPOSITORIES)}
            description={lang.SELECT_A_BACKUP_SERVER_TO_ASSIGN_TO_THE_COMPANY}
            actions={[
                {
                    text: lang.APPLY,
                    onClick: () => {
                        data.vboResources.forEach(res => res.vboRepositories = repositories.filter(rep => rep.vboInstanceUid === res.vboInstanceUid));

                        onDataChange(data);

                        hidePanel();
                    },
                },
                {
                    text: lang.CANCEL,
                    view: ACTION_VIEW.secondary,
                    onClick: hidePanel,
                },
            ]}
        >
            <AdvancedGrid <VboServerRepository, void, void>
                defaultColumnWidth={100}
                columns={[
                    {
                        title: capitalize(lang.BACKUP_PROXY),
                        field: 'proxy',
                        sorterFn(a, b) {
                            const first = getVboProxy(
                                a.repositoryUid,
                                wizardStore.availableVboProxiesCache.value,
                                wizardStore.availableVboBackupRepositoryCache.value
                            )?.name;

                            const second = getVboProxy(
                                b.repositoryUid,
                                wizardStore.availableVboProxiesCache.value,
                                wizardStore.availableVboBackupRepositoryCache.value
                            )?.name;

                            return `${first}`.localeCompare(`${second}`, 'kn', { numeric: true, sensitivity: 'base' });
                        },
                        cell: ({ rowData }) => (
                            <TextColumn>
                                {getVboProxy(
                                    rowData.repositoryUid,
                                    wizardStore.availableVboProxiesCache.value,
                                    wizardStore.availableVboBackupRepositoryCache.value
                                )?.name}
                            </TextColumn>
                        ),
                    },
                    {
                        title: capitalize(lang.BACKUP_REPOSITORY),
                        field: 'repository',
                        sorterFn(a, b) {
                            const first = wizardStore.availableVboBackupRepositoryCache.value
                                ?.find(item => item.instanceUid === a.repositoryUid)?.name;
                            const second = wizardStore.availableVboBackupRepositoryCache.value
                                ?.find(item => item.instanceUid === b.repositoryUid)?.name;

                            return `${first}`.localeCompare(`${second}`, 'kn', { numeric: true, sensitivity: 'base' });
                        },
                        cell: ({ rowData }) => (
                            <TextColumn>
                                {wizardStore.availableVboBackupRepositoryCache.value?.find(item => item.instanceUid === rowData.repositoryUid)?.name}
                            </TextColumn>
                        ),
                    },
                    {
                        title: lang.TYPE,
                        field: 'storageType',
                        sorterFn(a, b) {
                            const firstStorageType = wizardStore.availableVboBackupRepositoryCache.value
                                ?.find(item => item.instanceUid === a.repositoryUid)?.storageType;
                            const first = vb365RepositoryStorageTypes.find(item => item.id === firstStorageType)?.name;

                            const secondStorageType = wizardStore.availableVboBackupRepositoryCache.value
                                ?.find(item => item.instanceUid === b.repositoryUid)?.storageType;
                            const second = vb365RepositoryStorageTypes.find(item => item.id === secondStorageType)?.name;

                            return first.localeCompare(second, undefined, { sensitivity: 'base' });
                        },
                        cell: ({ rowData }) => {
                            const storageType = wizardStore.availableVboBackupRepositoryCache.value
                                ?.find(item => item.instanceUid === rowData.repositoryUid)?.storageType;
                            return <TextColumn>{vb365RepositoryStorageTypes.find(item => item.id === storageType)?.name}</TextColumn>;
                        },
                    },
                ]}
                data={() => Promise.resolve({
                    data: repositories,
                    meta: {
                        pagingInfo: {
                            total: repositories?.length,
                            count: repositories?.length,
                            offset: 0,
                        },
                    },
                })}
                toggleable={false}
                disablePagination={true}
                disableAutoUpdate={true}
                selection={{
                    field: 'repositoryUid',
                    multiple: false,
                    checkbox: false,
                }}
                toolbars={[
                    () => <ActionsToolbar items={repositories} vboResources={data.vboResources} />,
                ]}
                api={api}
                isLoading={loading}
                sortingMode={SortingMode.Local}
            />
        </SidePanelForm>
    );
});
